import React, { useState, useRef, useContext, useEffect } from 'react';
import { Stage, Layer, Image as KonvaImage, Line, Rect } from 'react-konva';
import { authFetch } from '../../Utilities/Api';
import AuthContext from '../../context/AuthContext';
import './ItemBuilder.css';

const canvasSize = 1024; // Base canvas size

const ItemBuilder = () => {
    const { user } = useContext(AuthContext);
    const fileInputRef = useRef(null);

    const [name, setName] = useState('');
    const [tags, setTags] = useState('');
    const [type, setType] = useState('hat');
    const [zoom, setZoom] = useState(1);
    const [snappingEnabled, setSnappingEnabled] = useState(true);
    const [showBaseAvatar, setShowBaseAvatar] = useState(true);
    const [previewEnabled, setPreviewEnabled] = useState(true);
    const [baseAvatarImage, setBaseAvatarImage] = useState(null);
    const [layers, setLayers] = useState([]);
    const [layerImages, setLayerImages] = useState({});

    // Load base avatar image once
    useEffect(() => {
        const img = new window.Image();
        img.src = '/default-avatar.png';
        img.onload = () => setBaseAvatarImage(img);
    }, []);

    // Add a new layer
    const addLayer = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            const url = URL.createObjectURL(file);
            const id = Date.now();

            const img = new window.Image();
            img.src = url;
            img.onload = () => {
                setLayerImages((prev) => ({ ...prev, [id]: img }));
            };

            setLayers((prev) => [
                ...prev,
                {
                    id,
                    url,
                    name: file.name || `Layer ${prev.length + 1}`,
                    pos: { x: 200, y: 200 }
                }
            ]);
            // reset input so same file can be re-added if desired
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    // Handle dragging end event for layers
    const handleDragEnd = (id, e) => {
        let { x, y } = e.target.position();
        if (snappingEnabled) {
            x = Math.round(x / 10) * 10;
            y = Math.round(y / 10) * 10;
        }

        setLayers((prev) =>
            prev.map((layer) => (layer.id === id ? { ...layer, pos: { x, y } } : layer))
        );
    };

    // Remove a layer (now used in UI)
    const removeLayer = (id) => {
        setLayers((prev) => prev.filter((layer) => layer.id !== id));
        setLayerImages((prev) => {
            const updated = { ...prev };
            delete updated[id];
            return updated;
        });
    };

    const clearAllLayers = () => {
        setLayers([]);
        setLayerImages({});
    };

    // Save the current item (draft or submit for review)
    const handleSave = async (submitted = false) => {
        if (!name || layers.length === 0) {
            alert('Name and at least one image are required.');
            return;
        }

        try {
            const res = await authFetch.get('/api/avatars/user-item-count');
            if (res.data.count >= 10 && !['admin', 'owner'].includes(user.role)) {
                return alert(
                    '🚫 Submission limit reached (10 items). Please wait for approval or delete a draft.'
                );
            }

            const payload = {
                name,
                type,
                tags: tags.split(',').map((tag) => tag.trim()).filter(Boolean),
                status: submitted ? 'pending' : 'draft',
                style: 'casual',
                available: false,
                rarity: 'starter',
                creator: user._id,
                parts: layers.map((l, index) => ({
                    image: l.url,
                    x: l.pos.x,
                    y: l.pos.y,
                    anchor: 'center',
                    zIndex: index
                }))
            };

            await authFetch.post('/api/avatars/create', payload);
            alert(submitted ? '✅ Item submitted for review!' : '✅ Draft saved!');
        } catch (err) {
            console.error('❌ Save failed:', err);
            alert('Failed to save item.');
        }
    };

    // Render grid for the canvas
    const renderGrid = () => {
        const spacing = 20;
        const lines = [];
        for (let i = 0; i <= canvasSize * zoom * 2; i += spacing) {
            lines.push(
                <Line key={`v-${i}`} points={[i, 0, i, canvasSize * zoom * 2]} stroke="#444" strokeWidth={1} />,
                <Line key={`h-${i}`} points={[0, i, canvasSize * zoom * 2, i]} stroke="#444" strokeWidth={1} />
            );
        }
        return lines;
    };

    return (
        <div className="item-builder-wrapper">
            <div className="builder-sidebar">
                <h2>Item Builder</h2>

                <label>Item Name</label>
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Silver Crown" />

                <label>Upload PNG</label>
                <input type="file" accept="image/png" onChange={addLayer} ref={fileInputRef} />

                <label>Layer (Type)</label>
                <select value={type} onChange={(e) => setType(e.target.value)}>
                    <option value="hat">Hat</option>
                    <option value="shirt">Shirt</option>
                    <option value="pants">Pants</option>
                    <option value="shoes">Shoes</option>
                    <option value="accessory">Accessory</option>
                    <option value="hair">Hair</option>
                    <option value="eyes">Eyes</option>
                    <option value="mouth">Mouth</option>
                    <option value="background">Background</option>
                </select>

                <label>Tags (comma separated)</label>
                <input value={tags} onChange={(e) => setTags(e.target.value)} placeholder="shiny, crown" />

                <label>Zoom: {Math.round(zoom * 100)}%</label>
                <input
                    type="range"
                    min="0.25"
                    max="2"
                    step="0.05"
                    value={zoom}
                    onChange={(e) => setZoom(parseFloat(e.target.value))}
                />

                {/* Simple layer list with remove buttons */}
                {layers.length > 0 && (
                    <>
                        <div className="layers-header">
                            <strong>Layers</strong>
                            <button type="button" onClick={clearAllLayers} className="clear-all">
                                Clear All
                            </button>
                        </div>
                        <ul className="layers-list">
                            {layers.map((l, i) => (
                                <li key={l.id} className="layer-row">
                                    <span className="layer-name">{l.name || `Layer ${i + 1}`}</span>
                                    <button
                                        type="button"
                                        className="remove-layer"
                                        onClick={() => removeLayer(l.id)}
                                        aria-label={`Remove ${l.name || `layer ${i + 1}`}`}
                                    >
                                        ❌
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </>
                )}

                <button onClick={() => handleSave(false)}>💾 Save Draft</button>
                <button onClick={() => handleSave(true)} className="submit">
                    📤 Submit
                </button>
            </div>

            <div className="builder-canvas-area">
                <div className="canvas-controls">
                    <label>
                        <input
                            type="checkbox"
                            checked={snappingEnabled}
                            onChange={() => setSnappingEnabled(!snappingEnabled)}
                        />
                        Snapping
                    </label>
                    <label>
                        <input
                            type="checkbox"
                            checked={previewEnabled}
                            onChange={() => setPreviewEnabled(!previewEnabled)}
                        />
                        Enable Preview
                    </label>
                    <label>
                        <input
                            type="checkbox"
                            checked={showBaseAvatar}
                            onChange={() => setShowBaseAvatar(!showBaseAvatar)}
                        />
                        Show Base Avatar
                    </label>
                </div>

                <Stage
                    width={canvasSize * zoom * 2}
                    height={canvasSize * zoom * 2}
                    scaleX={zoom}
                    scaleY={zoom}
                    className="canvas-stage"
                >
                    <Layer>
                        <Rect width={canvasSize * 2} height={canvasSize * 2} fill="white" />
                        {snappingEnabled && renderGrid()}
                        {showBaseAvatar && baseAvatarImage && <KonvaImage image={baseAvatarImage} />}
                        {previewEnabled &&
                            layers.map((layer) => {
                                const img = layerImages[layer.id];
                                return img ? (
                                    <KonvaImage
                                        key={layer.id}
                                        image={img}
                                        x={layer.pos.x}
                                        y={layer.pos.y}
                                        draggable
                                        onDragEnd={(e) => handleDragEnd(layer.id, e)}
                                    />
                                ) : null;
                            })}
                    </Layer>
                </Stage>
            </div>
        </div>
    );
};

export default ItemBuilder;
